import type { InfiniteTableProps } from "@galaxy-io/dls/table/InfiniteTable";
import { readSort, type SortQuery } from "@gtm/contracts/core/sorting";
import { useTransition } from "react";
import { queryHref, usePathname, useRouter } from "@/app/navigation";
import { useUrlState } from "./use-url-state";

export type TableSort = {
  columns: readonly string[];
  sorting: InfiniteTableProps<unknown>["sort"];
  onSortingChange: NonNullable<InfiniteTableProps<unknown>["onSortChange"]>;
  pending: boolean;
};
export function useTableSort<Key extends string>(
  columns: readonly Key[],
  {
    server = false,
    scope = "",
    defaultSort = {},
  }: { server?: boolean; scope?: string; defaultSort?: SortQuery<Key> } = {},
) {
  const { params, update } = useUrlState();
  const router = useRouter(),
    path = usePathname();
  const [pending, startTransition] = useTransition();
  const sortKey = scope ? `${scope}Sort` : "sort";
  const directionKey = scope ? `${scope}Direction` : "direction";
  const pageKey = scope ? `${scope}Page` : "page";
  const explicit = readSort(columns, {
    sort: params.get(sortKey) || undefined,
    direction: params.get(directionKey) || undefined,
  });
  const query = explicit.sort ? explicit : defaultSort;
  const sorting = query.sort
    ? { columnId: query.sort, isDescending: query.direction === "desc" }
    : null;
  const onSortingChange: TableSort["onSortingChange"] = (requested) => {
    let next = requested;
    // A default date order must still toggle when its descending state is clicked.
    if (!next && sorting?.columnId === defaultSort.sort && defaultSort.sort)
      next = { columnId: defaultSort.sort, isDescending: false };
    const changes = {
      [sortKey]: next?.columnId || null,
      [directionKey]: next ? (next.isDescending ? "desc" : "asc") : null,
      [pageKey]: null,
    };
    if (server)
      startTransition(() =>
        router.push(queryHref(path, new URLSearchParams(window.location.search), changes), {
          scroll: false,
        }),
      );
    else update(changes);
  };
  return { columns, sorting, onSortingChange, pending, query };
}
